import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import {
  Home,
  Book,
  Scale,
  Users,
  Scroll,
  Map,
  Sparkles,
  Swords,
  BookOpen,
  Search,
  Terminal,
  WandSparkles,
} from 'lucide-react';
import { createPageUrl } from '@/utils/routing';
import { useLastVisited } from '@/hooks/useLastVisited';
import { cn } from '@/lib/utils';

const FOCUS_COMPENDIUM_SEARCH = 'mythkit_focus_compendium_search';

export function setFocusCompendiumSearchFlag() {
  try {
    sessionStorage.setItem(FOCUS_COMPENDIUM_SEARCH, '1');
  } catch {
    // ignore
  }
}

/** Returns true once when the command palette requested compendium search focus; clears the flag. */
export function consumeFocusCompendiumSearch(): boolean {
  try {
    if (sessionStorage.getItem(FOCUS_COMPENDIUM_SEARCH) === '1') {
      sessionStorage.removeItem(FOCUS_COMPENDIUM_SEARCH);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

export function CommandPalette() {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const { lastCharacter, lastCampaign } = useLastVisited();

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', down);
    return () => window.removeEventListener('keydown', down);
  }, []);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const paletteClass =
    '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-text-muted [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide';

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Command menu"
      contentClassName={cn(
        'fixed left-1/2 top-[20%] z-dropdown w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 overflow-hidden p-0',
        'rounded-[var(--radius-dialog)] border border-border-soft bg-bg-surface shadow-modal',
        'dark:bg-bg-elevated',
        'focus:outline-none',
        paletteClass
      )}
      overlayClassName="fixed inset-0 z-modal bg-black/40 backdrop-blur-sm"
    >
      <div className="border-b border-border-default px-3 py-2 flex items-center gap-2">
        <Search className="w-4 h-4 shrink-0 text-text-muted" aria-hidden />
        <Command.Input
          placeholder="Search commands…"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-text-muted min-w-0"
        />
        <kbd className="hidden sm:inline text-[10px] text-text-muted border border-border-default rounded px-1.5 py-0.5">
          Esc
        </kbd>
      </div>
      <Command.List className="max-h-[min(50vh,360px)] overflow-y-auto p-2 scrollbar-thin">
        <Command.Empty className="py-6 text-center text-sm text-text-muted">No results.</Command.Empty>

        <Command.Group heading="Go to">
          <Command.Item
            value="home"
            onSelect={() => go('/')}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm aria-selected:bg-forest-green/15 aria-selected:text-forest-green"
          >
            <Home className="w-4 h-4 shrink-0" />
            Home
          </Command.Item>
          <Command.Item
            value="compendium"
            onSelect={() => {
              setFocusCompendiumSearchFlag();
              go(createPageUrl('Compendium'));
            }}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm aria-selected:bg-forest-green/15 aria-selected:text-forest-green"
          >
            <Book className="w-4 h-4 shrink-0" />
            Compendium (focus search)
          </Command.Item>
          <Command.Item
            value="rules"
            onSelect={() => go(createPageUrl('Rules'))}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm aria-selected:bg-forest-green/15 aria-selected:text-forest-green"
          >
            <Scale className="w-4 h-4 shrink-0" />
            Rules
          </Command.Item>
          <Command.Item
            value="characters"
            onSelect={() => go(createPageUrl('MyCharacters'))}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm aria-selected:bg-forest-green/15 aria-selected:text-forest-green"
          >
            <Users className="w-4 h-4 shrink-0" />
            My Characters
          </Command.Item>
          <Command.Item
            value="create character"
            onSelect={() => go(createPageUrl('CharacterCreator'))}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm aria-selected:bg-forest-green/15 aria-selected:text-forest-green"
          >
            <Scroll className="w-4 h-4 shrink-0" />
            Create Character
          </Command.Item>
          <Command.Item
            value="campaigns"
            onSelect={() => go(createPageUrl('Campaigns'))}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm aria-selected:bg-forest-green/15 aria-selected:text-forest-green"
          >
            <Map className="w-4 h-4 shrink-0" />
            Campaigns
          </Command.Item>
          <Command.Item
            value="generators"
            onSelect={() => go(createPageUrl('Generators'))}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm aria-selected:bg-forest-green/15 aria-selected:text-forest-green"
          >
            <Sparkles className="w-4 h-4 shrink-0" />
            Generators
          </Command.Item>
          <Command.Item
            value="spell book"
            onSelect={() => go(createPageUrl('SpellBook'))}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm aria-selected:bg-forest-green/15 aria-selected:text-forest-green"
          >
            <WandSparkles className="w-4 h-4 shrink-0" />
            Spell Book
          </Command.Item>
          <Command.Item
            value="combat"
            onSelect={() => go(createPageUrl('CombatTracker'))}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm aria-selected:bg-forest-green/15 aria-selected:text-forest-green"
          >
            <Swords className="w-4 h-4 shrink-0" />
            Combat Tracker
          </Command.Item>
          <Command.Item
            value="more tools"
            onSelect={() => go(createPageUrl('More'))}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm aria-selected:bg-forest-green/15 aria-selected:text-forest-green"
          >
            <BookOpen className="w-4 h-4 shrink-0" />
            Library & tools
          </Command.Item>
          <Command.Item
            value="macros"
            onSelect={() => go(createPageUrl('Macros'))}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm aria-selected:bg-forest-green/15 aria-selected:text-forest-green"
          >
            <Terminal className="w-4 h-4 shrink-0" />
            Macros
          </Command.Item>
        </Command.Group>

        {(lastCharacter || lastCampaign) && (
          <Command.Group heading="Continue">
            {lastCharacter && (
              <Command.Item
                value={`last character ${lastCharacter.name}`}
                onSelect={() =>
                  go(`${createPageUrl('CharacterSheet')}?id=${encodeURIComponent(lastCharacter.id)}`)
                }
                className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm aria-selected:bg-forest-green/15 aria-selected:text-forest-green"
              >
                <Users className="w-4 h-4 shrink-0" />
                Last character: {lastCharacter.name}
              </Command.Item>
            )}
            {lastCampaign && (
              <Command.Item
                value={`last campaign ${lastCampaign.name}`}
                onSelect={() =>
                  go(`${createPageUrl('CampaignDetail')}?id=${encodeURIComponent(lastCampaign.id)}`)
                }
                className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm aria-selected:bg-forest-green/15 aria-selected:text-forest-green"
              >
                <Map className="w-4 h-4 shrink-0" />
                Last campaign: {lastCampaign.name}
              </Command.Item>
            )}
          </Command.Group>
        )}
      </Command.List>
      <div className="border-t border-border-default px-3 py-2 text-[11px] text-text-muted flex flex-wrap gap-x-3 gap-y-1">
        <span>
          <kbd className="font-mono rounded border border-border-default px-1">⌘</kbd> /{' '}
          <kbd className="font-mono rounded border border-border-default px-1">Ctrl</kbd>{' '}
          <kbd className="font-mono rounded border border-border-default px-1">K</kbd> toggle
        </span>
        <span>
          <kbd className="font-mono rounded border border-border-default px-1">?</kbd> shortcuts
        </span>
      </div>
    </Command.Dialog>
  );
}
